import { Component, HostBinding, Input, OnChanges, SimpleChanges } from "@angular/core";
import { TemperatureService } from "@temperature/_services/temperature.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { Moment } from "moment";
import { ITemperatureRecord } from "@temperature/_models/temperature/ITemperatureRecord";
import './temperatureReading.scss';
import { TemperatureProbeCategory } from "@temperature/_enums/temperature/TemperatureProbeCategory.enum";
import { ITemperatureProbe } from "@temperature/_models/temperature/ITemperatureProbe";
import { TemperatureProbeService } from "@temperature/_services/temperature-probe.service";

@Component({
    selector: 'temperature-reading',
    template: require('./temperatureReading.html')
})

export class TemperatureReadingComponent implements OnChanges {

    @Input() temperatureRecordId: number;
    @Input() temperatureProbe: ITemperatureProbe;
    @Input() temperatureDate: Moment;
    @Input() temperatureReading: number;
    @Input() isDateWarn = false;
    @Input() reefer = false;
    @Input() probeName: string;

    @HostBinding('class.text-muted') mutedClass = false;

    debounceTimeout: any;
    label: string = '';

    updating = false;

    temperatureRecord: ITemperatureRecord;

    constructor(
        private readonly temperatureService: TemperatureService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService) { }

    ngOnInit(): void {
        this.render();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.render();
    }

    private render(): void {
        // We want a small debounce here, this protects us against fast scrolling - no point updating a temperature if we have just scrolled past it
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        this.debounceTimeout = setTimeout(() => {
            this.updateLabel(true);
            this.debounceTimeout = null;
        }, 100);
    }

    private updateLabel(showUpdatingText: boolean) {
        if (this.temperatureReading !== null && !isNaN(this.temperatureReading)) {
            this.setLabel();
        }
        else {
            this.updating = true;
            this.getTemperatureRecord();
        }
    }

    private setLabel() {
        if (this.temperatureReading == null) {
            this.label = this.translationService.t('TemperatureReading.NoRecordFound');;
            this.updating = false;
            this.mutedClass = false;
            return;
        }
        this.setProbeName();
        this.label = `${this.unitConversionService.smartConvert(this.temperatureReading, UnitConversionType.Temperature, { precision: 1 })}`;
        this.updating = false;
        this.mutedClass = false;
    }

    private getTemperatureRecord(): Promise<void | ITemperatureRecord> {
        if (this.updating) {
            this.mutedClass = true;
            this.label = this.translationService.t('TemperatureReading.Updating');
        }

        if (!this.temperatureRecordId) {
            this.mutedClass = true;
            this.label = "--";
            return;
        }

        return new Promise((resolve) => {
            this.temperatureService.getTemperatureRecord(this.temperatureRecordId)
                .then((temperatureRecord) => {
                    if (!temperatureRecord) {
                        return;
                    }
                    this.temperatureRecord = temperatureRecord;
                    this.temperatureReading = temperatureRecord.Temperature;
                    this.setLabel();
                    !this.temperatureProbe && this.getProbe();
                    this.setProbeName();
                    resolve(temperatureRecord);
                }).catch(error => {
                    this.label = "--";
                });
        });
    }

    private setProbeName() {
        if (!this.temperatureProbe || this.probeName) {
            return;
        }

        if (!this.reefer) {
            this.probeName = this.temperatureProbe.cleanName;
        }
        else {
            this.probeName = `${TemperatureProbeCategory[this.temperatureProbe.Category]}`;
        }
    }

    private getProbe() {
        this.temperatureProbeService.getTemperatureProbesForVehicle(this.temperatureRecord.VehicleId).subscribe(probes => {
            this.temperatureProbe = probes.find(p => p.Id === this.temperatureRecord.TemperatureProbeId);
            this.setProbeName();
        });
    }
}
